import { type VariantProps, cva } from 'class-variance-authority';
import { clsx } from 'clsx';
import { forwardRef } from 'react';

export const buttonVariants = cva(
  'inline-flex items-center justify-center gap-2 font-medium rounded-lg transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-border-focus disabled:pointer-events-none disabled:bg-disabled disabled:text-disabled-fg',
  {
    variants: {
      variant: {
        primary:
          'bg-cherry-500 text-white hover:bg-cherry-600 active:bg-cherry-700',
        secondary:
          'bg-surface-raised text-text-primary border border-border hover:bg-surface-alt active:bg-surface',
        success:
          'bg-success text-white hover:brightness-110 active:brightness-90',
        warning:
          'bg-warning text-white hover:brightness-110 active:brightness-90',
        destructive:
          'bg-error text-white hover:brightness-110 active:brightness-90',
        ghost:
          'bg-transparent text-text-primary hover:bg-surface-alt active:bg-surface',
      },
      size: {
        sm: 'text-sm px-3 py-1.5 min-h-9',
        md: 'text-sm px-4 py-2 min-h-11',
        lg: 'text-base px-6 py-3 min-h-13',
      },
    },
    defaultVariants: {
      variant: 'primary',
      size: 'md',
    },
  },
);

interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  loading?: boolean;
  loadingText?: string;
  fullWidth?: boolean;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  function Button(
    {
      variant,
      size,
      loading,
      loadingText,
      fullWidth,
      className,
      disabled,
      children,
      ...props
    },
    ref,
  ) {
    return (
      <button
        ref={ref}
        disabled={disabled || loading}
        className={clsx(buttonVariants({ variant, size }), fullWidth && 'w-full', className)}
        {...props}
      >
        {loading ? (
          <span className="flex items-center justify-center gap-2">
            <svg
              className="h-4 w-4 animate-spin"
              viewBox="0 0 24 24"
              fill="none"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              />
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              />
            </svg>
            {loadingText ?? 'Processing...'}
          </span>
        ) : (
          children
        )}
      </button>
    );
  },
);
